import { IonRow, IonCol } from "@ionic/react";
import { ChartData, ChartDataset, ChartOptions, Plugin } from "chart.js";
import { Page }              from "components/Page";
import { MeasurementsTable } from "components/MeasurementsTable";
import { DvcPage }           from "navigation/DvcPage";
import { RouterMap }         from "navigation/RouterMap";
import { Glob, useLoc }           from "functions/Language";
import { FormatNumberSI } from "functions/Formatting";
import { IsFromDevice, TYPE_PLA, OnlineDataPLA } from "functions/networking/NetTypes";
import { useLoader }              from "functions/networking/useLoader";
import { FC, useMemo } from "react";
import { Scatter } from "react-chartjs-2";

import vector from "img/vectors.svg";


const route = RouterMap.vector;


const colors = ["#ff0000", "#00ee00", "#3f3fff"];
function p2c(angles: [number, number, number], magnitudes: [number, number, number], type: "I" | "U") : ChartDataset<"scatter">[] {
  return [0, 1, 2].map(i => ({
    data: [
      { x: 0, y: 0 }, // slouží pouze k nakreslení průvodiče
      {
        x: magnitudes[i%3] * Math.cos(angles[i%3] * Math.PI / 180),
        y: -magnitudes[i%3] * Math.sin(angles[i%3] * Math.PI / 180)
      }
    ],
    xAxisID: `x${type}`,
    yAxisID: `y${type}`,
    borderColor: colors[i],
    backgroundColor: colors[i],
    borderDash: type === "I" ? [10, 6] : undefined,
    borderWidth: 2,
    showLine: true,
    label: `${type}  L${i + 1}`,
    pointHitRadius: [0, 30],
    pointRadius: [0, 3],
    //pointStyle: type === "I" ? "rect" : "circle"
  }));
}

const PolarChart : FC<{
  data: OnlineDataPLA
}> = (props) => {
  const loc = useLoc(glob);

  const data_options = useMemo(() => {
    let urange = Math.max(...props.data.U_LN, .1) * 1.02;
    let irange = Math.max(...props.data.Current, .01) * 1.02;
    let axisSteps = GetRadialAxisSteps(urange, irange);
    urange = axisSteps.ustep * axisSteps.steps;
    irange = axisSteps.istep * axisSteps.steps;

    const udata = p2c(props.data.Angle_U, props.data.U_LN, "U");
    const idata = p2c(props.data.Angle_I, props.data.Current, "I");

    let res: {data: ChartData<"scatter">, options: ChartOptions<"scatter">, axisSteps : typeof axisSteps} = {
      axisSteps: axisSteps,
      data: {
        datasets: [
          ...udata,
          ...idata
        ],
      },
      options: {
        /*transitions: {
          show: {
            animation: undefined
          },
          hide: {
            animation: undefined,
          }
        },*/
        scales: {
          "xU": {
            min: -urange,
            max: urange,
            display: false
          },
          "yU": {
            min: -urange,
            max: urange,
            display: false
          },
          "xI": {
            min: -irange,
            max: irange,
            display: false
          },
          "yI": {
            min: -irange,
            max: irange,
            display: false
          }
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label(tooltipItem) {
                return " " + tooltipItem.dataset.label;
              },
              afterLabel(tooltipItem) {
                const mag = Math.sqrt((tooltipItem.parsed.x**2) + (tooltipItem.parsed.y**2));
                let angle = Math.atan2(-tooltipItem.parsed.y, tooltipItem.parsed.x) * 180 / Math.PI;
                if (isNaN(angle)) angle = 0;
                if (angle < 0) angle += 360;
                const type = tooltipItem.dataset.label?.at(0) === "I" ? "A" : "V"
                return `${type === "A" ? loc.curr : loc.volt}: ${FormatNumberSI(mag)}${type}\n${loc.angle}: ${angle.toFixed(1)}°`; // TODO: zjistit uhlovou konvenci
              },
            }
          }
        },
        aspectRatio: 1,
      }
    }

    return res;
  }, [props.data])

  return <>
    <div style={{width: "min(calc(100% - 20px), 80vh)", padding: "10px", position: "relative", marginInline: "auto"}}>
      <div style={{paddingTop: "100%"}}></div>
      <Scatter data={data_options.data} options={data_options.options} plugins={[PolarAxes]} style={{position: "absolute", top: 0}} />

      <IonRow>
        <IonRow style={{minWidth: "18em", flex: 1}}>
          {colors.map((c, i) => <LegendItem color={c} label={`U${i + 1}`} key={i} />)}
        </IonRow>
        <IonRow style={{minWidth: "18em", flex: 1}}>
          {colors.map((c, i) => <LegendItem color={c} label={` I${i + 1}`} dashed key={i} />)}
        </IonRow>
      </IonRow>
    </div>
  </>
}

const LegendItem : FC<{
  color : string,
  label : string,
  dashed? : boolean,
}> = (props) => {
  return <IonCol>
    <span style={{
      marginBottom: "calc(1ex - 3px)",
      marginInline: "5px",
      width: "3em",
      borderBottom: `${props.dashed ? "dashed" : "solid"} ${props.color} 2px`,
      display: "inline-block"
    }}/>
    <span>{props.label}</span>
  </IonCol>
}

const PolarAxes: Plugin<"scatter"> = {
  id: "PolarAxes",
  beforeDraw: (chart) => {
    const ctx = chart.ctx;
    const { top, left, width, height } = chart.chartArea;
    const zero = { x: left + width / 2, y: top + height / 2 }
    const len = width / 2;

    ctx.lineWidth = 1;
    ctx.strokeStyle = "#bbb"
    ctx.moveTo(zero.x - len, zero.y);
    ctx.lineTo(zero.x + len, zero.y);
    const a = len / 2;                // cos 60° = sin 30°
    const b = Math.sqrt(3) * len / 2; // cos 30° = sin 60°
    ctx.moveTo(zero.x - a, zero.y - b);
    ctx.lineTo(zero.x + a, zero.y + b);
    ctx.moveTo(zero.x + a, zero.y - b);
    ctx.lineTo(zero.x - a, zero.y + b);
    ctx.stroke();

    ctx.beginPath();
    for (let i = 1; i <= 5; i++) {
      ctx.ellipse(zero.x, zero.y, i * len / 5, i * len / 5, 0, 0, 2 * Math.PI);
    }
    ctx.stroke();

    let scales = chart.config.options?.scales;
    let urange = 1, irange = 1;
    if (scales !== undefined) {
      let _range = scales["xU"]?.max;
      if (typeof _range === "number")
        urange = _range;
      _range = scales["xI"]?.max;
      if (typeof _range === "number")
        irange = _range;
    }
    urange /= 5;
    irange /= 5;
    ctx.fillStyle = "#bbb";
    ctx.save();
    ctx.textAlign = "center";
    ctx.translate(len, len);
    //ctx.rotate(.3);
    ctx.textBaseline = "top";
		let decimals = DecimalPlaces(urange);
    for (let i = 1; i <= 5; i++) {
      ctx.fillText(`${(urange * i).toFixed(decimals)} V`, 8, -len / 5 * i + 10);
    }
    //ctx.rotate(-.6);
    ctx.textBaseline = "alphabetic";
		decimals = DecimalPlaces(irange);
    for (let i = 1; i <= 5; i++) {
      ctx.fillText(`${(irange * i).toFixed(decimals)} A`, 8, len / 5 * i);
    }
    ctx.restore();
  }
}

function GetOrder(n: number) {
  let o = 1;
  if (n > 0) {
    if (n < 1) {
      while (n / o < 1) o /= 10;
    } else {
      while (n / o >= 1) o *= 10;
      o /= 10;
    }
  }
  if (n / o > 5) o *= 5;
  if (n / o > 2) o *= 2;
  return o;
}

function StepSize(m: number) {
	const result
		= m < 1.15 ? .25
		: m < 1.4  ? .3
		: m < 1.75 ? .4
		:            .5
		;
	return result;
}

function GetRadialAxisSteps(urange: number, irange: number) {
  let ou = GetOrder(urange);
  let oi = GetOrder(irange * 1.5);
  let ustep = StepSize(urange / ou) * ou;
  let istep = StepSize(irange * 1.5 / oi) * oi;
  return { ustep, istep, steps: Math.floor(Math.max(urange / ustep, irange / istep)) + 1 };
  // steps by mělo být vždy 5
  // pokud ne, někdo nám to řekne
}

function DecimalPlaces(step: number) {
	const firstLabel = step.toString();
	const dot        = firstLabel.indexOf('.');
	if (dot < 0) return 0;
	return firstLabel.length - dot - 1;
}



const Content : FC = () => {
  const loc = useLoc(glob);
  const loader = useLoader("Online", null);

  const loaders = [loader];

  return <Page title={loc.title} loaders={loaders}>
    {loader.data === null || !IsFromDevice(loader.data, TYPE_PLA) ? "" :
      <IonRow>
        <PolarChart data={loader.data.Data}/>
        <MeasurementsTable data={loader.data} />
      </IonRow>
    }
  </Page>
}

const glob = Glob({
	en: {
		title: "Vector diagram",

		curr: "Current",
		volt: "Voltage",
		angle: "Angle"
	},
	cs: {
		title: "Vektorový diagram",

		curr: "Proud",
		volt: "Napětí",
		angle: "Úhel"
	},
	pg: {
		title: "Diagrama vetorial",

		curr: "Corrente",
		volt: "Tensão",
		angle: "Ângulo",
	},
	es: {
		title: "Diagrama vetorial",

		curr: "Corriente",
		volt: "Tensión",
		angle: "Ángulo",
	},
} as const);


export const PVector : DvcPage = {
  page: Content,
  path: route,
  title: glob,
  icon: vector,

	available(dvc) {
		return dvc._type === TYPE_PLA;
	}
}
